// ── Public website origin ────────────────────────────────────────────────────
// Use this for any URL a person will SHARE (referral links, affiliate links,
// share sheets) — never `window.location.origin` directly.
//
// Two ways this has gone wrong, both producing links that are dead for whoever
// receives them — on exactly the features whose whole purpose is to be shared:
//
//   1. Inside the native apps the WebView's own origin is "https://localhost",
//      so window.location.origin gave "https://localhost/?aff=CODE".
//
//   2. The fix for (1) trusted REACT_APP_CLIENT_URL first. But frontend/.env
//      sets that to http://localhost:3000 for local development, CRA loads .env
//      for EVERY environment, and .env.production did not override it — so the
//      production build shipped with localhost:3000 baked in, and every referral
//      and affiliate link on web AND in both apps pointed at a developer's
//      machine.
//
// So a configured value is no longer trusted blindly. A localhost URL is only
// ever returned when the page itself is running on localhost — which is the
// one situation where it is correct.

import { isNative } from './capacitor';

const PUBLIC_SITE_URL = 'https://dreamybedtime.com';

const LOCAL_HOST = /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(:\d+)?(\/|$)/i;
const isLocal = (url) => LOCAL_HOST.test(String(url || ''));

const clean = (url) => String(url || '').trim().replace(/\/+$/, '');

export const siteUrl = () => {
  const configured = clean(process.env.REACT_APP_CLIENT_URL);

  // ── Native: a phone can never reach anyone's localhost ─────────────────────
  // Neither the WebView origin (https://localhost) nor a dev URL is meaningful
  // to the person the link is sent to.
  if (isNative()) {
    return configured && !isLocal(configured) ? configured : PUBLIC_SITE_URL;
  }

  // ── Web ─────────────────────────────────────────────────────────────────────
  let here = '';
  try { here = window.location.origin; } catch (_) { /* non-browser */ }

  // Local development: localhost is genuinely right, whichever source says so.
  if (here && isLocal(here)) return configured || here;

  // Deployed: a localhost config is a leaked dev value — ignore it.
  if (configured && !isLocal(configured)) return configured;

  // The page's own origin is correct for production and for staging/preview
  // deploys alike, since the link then points back at the same deployment.
  return here && !isLocal(here) ? here : PUBLIC_SITE_URL;
};

export default siteUrl;
